/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import * as strings from '@touchplan/strings';
import { PlanState } from 'src/app/services/plan-state.service';

import { Ticket, Side } from '@touchplan/common';
import { logger } from '@touchplan/logging';
import { hasAccessToTicket, hasRestrictedAccess } from 'src/app/services/plan-access-check-utils';

//the idea is that this class will be created by planState, it will have access to it
//All types of interaction will be enumed out here and passed into a check function with whatever extra info that check needs

//ideally try and capture what actually happens here, not literal interactions
// (selection via double tapping and group box are the same thing generally)
export enum Interaction {
	TapPlan,
	MultiSelect,
	OpenEditTicket,
	SwitchEditTicket,
	DragTicketInPlan,
	TogglePredecessor,
	OnlyCheckVariancePrompt, //mainly just use in the special case of floating tickets to not mess with it
	OpenTicketContextMenu,
	DisplayTicketHoverInfo,
}

export enum ExternalData {
	IsEditValid = 'isEditValid',
}

export class InteractionBlocker {
	private shutdownSubject = new Subject<void>();

	constructor(private planState: PlanState) {}

	/** current set of essentailly live external data. It should always be safe to directly query properties */
	private externalData = {
		[ExternalData.IsEditValid]: false,
	};

	/** the data to start with and reset to when the external observable completes.
	 * This increases stability when data is being registered/ loaded. Ensure to default
	 * no "no access"
	 */
	private externalDataDefaults = {
		[ExternalData.IsEditValid]: false,
	};

	//return true if you can do the thing
	public check(type: Interaction, additionalInfo: any = {}) {
		const anyEditTickets =
			!!this.planState.tickets.editTicketCountSync || !!this.planState.floatingTickets.editTicketCountSync;
		// let variancePromptOpen = this.planState.ticketDragService.showVariancePrompt.getValue();

		const authedProjectMember = this.planState.authedProjectMemberSync;

		switch (type) {
			case Interaction.TapPlan:
				return (
					additionalInfo.tickets &&
					additionalInfo.tickets.size > 0 &&
					!anyEditTickets &&
					!this.checkVariancePromptAndShake()
				);
			case Interaction.MultiSelect:
				return additionalInfo.hasAccess && !anyEditTickets && !this.checkVariancePromptAndShake();
			case Interaction.DragTicketInPlan:
				//abort if read only

				const planId = this.planState.plan.syncObject.$id;
				const project = this.planState.project.syncObject;
				const planAccess = this.planState.plansApi.planAccessApi.data.syncList.get(planId);
				const isReadOnly = hasRestrictedAccess(project, planAccess, authedProjectMember);

				if (isReadOnly) {
					this.planState.scopeSoup.readOnlyReaction();
					return false;
				}
				//abort if anything is open in edit
				if (
					this.planState.tickets.editTicket$.getValue().size ||
					this.planState.floatingTickets.editTicket$.getValue().size
				) {
					return false;
				}

				const selectedTickets: Map<string, Ticket> = additionalInfo.selectedTickets;
				const interactedTicket: Ticket = additionalInfo.interactedTicket;

				//abort if the user doesn't have access to all the tickets
				let notAdminNotRole = !hasAccessToTicket(interactedTicket, authedProjectMember); //check interacted ticket
				selectedTickets.forEach((t) => {
					notAdminNotRole = notAdminNotRole || !hasAccessToTicket(t, authedProjectMember); //check selected tickets
				});
				if (notAdminNotRole) {
					return false;
				}

				//can't drag from a non-selected ticket when the variancePrompt is open
				if (this.checkVariancePromptAndShake() && !selectedTickets.has(interactedTicket.$id)) {
					return false;
				}

				return true;
			case Interaction.OpenEditTicket:
				return !anyEditTickets && !this.checkVariancePromptAndShake();
			case Interaction.SwitchEditTicket:
				const valid = this.externalData.isEditValid;
				if (!valid) {
					Logging.warning(strings.TICKET_ERRORS_EDIT_INVALID_SWITCH_TICKET);
				}
				return valid;
			case Interaction.OnlyCheckVariancePrompt:
				return !this.checkVariancePromptAndShake();
			case Interaction.TogglePredecessor:
				return !isReadOnly;
			case Interaction.OpenTicketContextMenu:
				if (!additionalInfo || !additionalInfo.tickets) {
					return false;
				}
				let hasPull = false;
				let noAccess = false;
				let actualized = false;

				additionalInfo.tickets.forEach((t: Ticket) => {
					if (t.view.side === Side.PULL) {
						hasPull = true;
					}
					if (!hasAccessToTicket(t, authedProjectMember)) {
						noAccess = true;
					}
					if (t.view.isActualized) {
						actualized = true;
					}
				});

				if (hasPull && additionalInfo.tickets.size > 1) {
					Logging.warning(strings.QUICK_STATUS_ERROR_PULL_TICKETS);
				}
				if (noAccess) {
					Logging.warning(strings.QUICK_STATUS_ERROR_NO_ACCESS);
				}
				if (actualized) {
					Logging.warning(strings.QUICK_STATUS_ERROR_ACTUALIZED);
				}

				return !(hasPull || noAccess || actualized);

			case Interaction.DisplayTicketHoverInfo:
				return true;

			default:
				break;
		}

		return false;
	}

	/**
	 * setup a way to register data that does not live on the planState.
	 * The input data must be an observable, and that observable is expected to
	 * complete when it's data is no longer valid. When adding a new peice of
	 * external data, remember to add the corresponding ExternalData, externalData,
	 * and externalDataDefaults. It's highly reccomended to create a wrapper
	 * convienence function for each type of input data.
	 * @param  data     An observable of arbitrary data, usually a boolean
	 */
	public registerData(dataType: ExternalData, data: Observable<any>) {
		const completion = () => {
			this.externalData[dataType] = this.externalDataDefaults[dataType];
		};

		data.pipe(takeUntil(this.shutdownSubject)).subscribe({
			next: (val) => {
				this.externalData[dataType] = val;
			},
			error: (err) => {
				logger.error('interaction-blocker registerData error. dataType: ', dataType, err);
				completion();
			},
			complete: completion,
		});
	}

	/** register the isEditValid data observable */
	public registerIsEditValid(data: Observable<boolean>) {
		this.registerData(ExternalData.IsEditValid, data);
	}

	private checkVariancePromptAndShake() {
		const variancePromptOpen = this.planState.ticketDragService.showVariancePrompt.getValue();
		if (variancePromptOpen) {
			this.planState.makeVariancePromptShake();
		}
		return variancePromptOpen;
	}

	public destroy() {
		this.shutdownSubject.next();
		this.planState = null;
	}
}
